import React from 'react';
import { X, Heart, MapPin, Ruler, Users, Building, Home, Check, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { useDeviceInfo } from '@/hooks/useDeviceInfo';
import { useLanguage } from '@/contexts/LanguageContext';
import { useResponsiveImage } from '@/hooks/useResponsiveImage';
import { useExchangeRate } from '@/hooks/useExchangeRate';
import { resolveImageUrl } from '@/utils/imageUtils';
import { formatPriceWithRate, convertSEKToUSD } from '@/utils/priceUtils';
import { Property } from '@/hooks/useProperties';
import { cn } from '@/lib/utils';

interface PropertyComparisonProps {
  properties: Property[];
  onRemove: (propertyId: string) => void;
  onClose: () => void;
  className?: string;
}

interface ComparisonRow {
  label: string;
  getValue: (property: Property, rate: number) => React.ReactNode;
  icon?: React.ReactNode;
}

export function PropertyComparison({ 
  properties, 
  onRemove, 
  onClose,
  className 
}: PropertyComparisonProps) {
  const { deviceCategory, isTouchDevice } = useDeviceInfo();
  const { t } = useLanguage();
  const { getOptimizedUrl } = useResponsiveImage();
  const { rate } = useExchangeRate();

  const isMobile = deviceCategory === 'phone';
  const isTablet = deviceCategory === 'tablet';

  const comparisonRows: ComparisonRow[] = [
    {
      label: t('property.price') || 'Price',
      getValue: (p, r) => (
        <span className="font-bold text-primary text-sm">
          {formatPriceWithRate(convertSEKToUSD(p.price), r)}
        </span>
      ),
    },
    {
      label: t('property.type') || 'Type',
      getValue: (p) => <Badge variant="secondary">{p.type}</Badge>,
      icon: <Home className="w-4 h-4" />,
    },
    {
      label: t('property.location') || 'Location',
      getValue: (p) => p.city,
      icon: <MapPin className="w-4 h-4" />,
    },
    {
      label: t('property.rooms_label') || 'Rooms',
      getValue: (p) => p.rooms,
      icon: <Users className="w-4 h-4" />,
    },
    {
      label: t('property.size_label') || 'Size',
      getValue: (p) => `${p.size} m²`,
      icon: <Ruler className="w-4 h-4" />,
    },
    {
      label: t('property.plot_size_label') || 'Plot Size',
      getValue: (p) => `${p.plot_size} m²`,
      icon: <Building className="w-4 h-4" />,
    },
    {
      label: t('property.price_per_sqm') || 'Price/m²',
      getValue: (p, r) => formatPriceWithRate(convertSEKToUSD(Math.round(p.price / p.size)), r),
    },
    {
      label: t('property.monthly_fee_label') || 'Monthly Fee',
      getValue: (p, r) => p.monthly_fee ? formatPriceWithRate(convertSEKToUSD(p.monthly_fee), r) : '-',
    },
  ];

  // Get all unique features across properties
  const allFeatures = [...new Set(properties.flatMap(p => p.features || []))];

  // Mobile: Vertical card stack with swipeable comparison
  if (isMobile) {
    return (
      <div className={cn('fixed inset-0 z-50 bg-background', className)}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">
            {t('property.compare') || 'Compare'} ({properties.length})
          </h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-60px)]">
          <div className="flex gap-4 p-4 pb-8">
            {properties.map((property) => (
              <Card key={property.id} className="flex-shrink-0 w-[280px]">
                <div className="relative">
                  <img
                    src={getOptimizedUrl(resolveImageUrl(property.image))}
                    alt={property.address}
                    className="w-full h-40 object-cover rounded-t-lg"
                  />
                  <Button
                    variant="destructive"
                    size="icon"
                    className="absolute top-2 right-2 w-8 h-8"
                    onClick={() => onRemove(property.id)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <CardContent className="p-4 space-y-3">
                  <div>
                    <h3 className="font-semibold truncate">{property.address}</h3>
                    <p className="text-sm text-muted-foreground">{property.city}</p>
                  </div>

                  {comparisonRows.map((row) => (
                    <div key={row.label} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground flex items-center gap-1">
                        {row.icon}
                        {row.label}
                      </span>
                      <span>{row.getValue(property, rate)}</span>
                    </div>
                  ))}

                  {allFeatures.length > 0 && (
                    <div className="pt-2 border-t">
                      <p className="text-xs text-muted-foreground mb-2">
                        {t('property.features') || 'Features'}
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {allFeatures.slice(0, 5).map((feature) => {
                          const hasFeature = property.features?.includes(feature);
                          return (
                            <Badge
                              key={feature}
                              variant={hasFeature ? 'default' : 'outline'}
                              className={cn(
                                'text-xs',
                                !hasFeature && 'opacity-40'
                              )}
                            >
                              {hasFeature ? <Check className="w-3 h-3 mr-1" /> : <Minus className="w-3 h-3 mr-1" />}
                              {feature}
                            </Badge>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
    );
  }

  // Tablet: Grid layout with 2 columns
  if (isTablet) {
    return (
      <div className={cn('fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-auto', className)}>
        <div className="container mx-auto p-4">
          <div className="flex items-center justify-between mb-4 sticky top-0 bg-background/95 backdrop-blur-sm py-2 z-10">
            <h2 className="text-xl font-semibold">
              {t('property.compare') || 'Compare Properties'} ({properties.length})
            </h2>
            <Button variant="outline" onClick={onClose}>
              <X className="w-4 h-4 mr-2" />
              {t('common.close') || 'Close'}
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {properties.map((property) => (
              <Card key={property.id} className="relative">
                <Button
                  variant="destructive"
                  size="icon"
                  className="absolute top-2 right-2 z-10"
                  onClick={() => onRemove(property.id)}
                >
                  <X className="w-4 h-4" />
                </Button>

                <img
                  src={getOptimizedUrl(resolveImageUrl(property.image))}
                  alt={property.address}
                  className="w-full h-48 object-cover rounded-t-lg"
                />

                <CardContent className="p-4">
                  <h3 className="font-semibold mb-1">{property.address}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{property.city}</p>

                  <div className="space-y-2">
                    {comparisonRows.map((row) => (
                      <div key={row.label} className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground flex items-center gap-2">
                          {row.icon}
                          {row.label}
                        </span>
                        <span className="text-sm">{row.getValue(property, rate)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Desktop: Side-by-side table comparison
  return (
    <div className={cn('fixed inset-0 z-50 bg-background/95 backdrop-blur-sm overflow-auto', className)}>
      <div className="container mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold">
            {t('property.compare') || 'Compare Properties'}
          </h2>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            {t('common.close') || 'Close'}
          </Button>
        </div>

        <div className="bg-card rounded-lg border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="p-4 text-left w-48 bg-muted/50">
                  {t('property.property') || 'Property'}
                </th>
                {properties.map((property) => (
                  <th key={property.id} className="p-4 text-center min-w-[200px]">
                    <div className="relative inline-block">
                      <img
                        src={getOptimizedUrl(resolveImageUrl(property.image))}
                        alt={property.address}
                        className="w-full h-32 object-cover rounded-lg mb-2"
                      />
                      <Button
                        variant="destructive"
                        size="icon"
                        className="absolute -top-2 -right-2 w-6 h-6"
                        onClick={() => onRemove(property.id)}
                      >
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                    <h3 className="font-semibold text-sm">{property.address}</h3>
                    <p className="text-xs text-muted-foreground">{property.city}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, index) => (
                <tr key={row.label} className={cn(index % 2 === 0 && 'bg-muted/30')}>
                  <td className="p-4 font-medium text-sm flex items-center gap-2">
                    {row.icon}
                    {row.label}
                  </td>
                  {properties.map((property) => (
                    <td key={property.id} className="p-4 text-center">
                      {row.getValue(property, rate)}
                    </td>
                  ))}
                </tr>
              ))}

              {/* Features comparison */}
              {allFeatures.length > 0 && (
                <>
                  <tr className="border-t">
                    <td colSpan={properties.length + 1} className="p-4 bg-muted/50 font-semibold text-sm">
                      {t('property.features') || 'Features'}
                    </td>
                  </tr>
                  {allFeatures.map((feature, index) => (
                    <tr key={feature} className={cn(index % 2 === 0 && 'bg-muted/30')}>
                      <td className="p-4 text-sm">{feature}</td>
                      {properties.map((property) => (
                        <td key={property.id} className="p-4 text-center">
                          {property.features?.includes(feature) ? (
                            <Check className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <Minus className="w-5 h-5 text-muted-foreground mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PropertyComparison;
